import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SinConexionKawaii from "../SinConexionKawaii";

// Template62: Página de mapa de Kamakura
// Copia DIRECTA del componente Page5.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template62 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [hasederaStickerError, setHasederaStickerError] = useState(false);
	const [logoError, setLogoError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const hasederaSticker = getCanvasImageUrl("hasedera-sticker");
	const mapUrl = page.maps?.[0];
	const explanationText = page.texts?.[1]?.content;
	const stickerHasederaText = page.texts?.[2]?.content;
	const contentColor = textColor || "#444";

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen) {
		// Vista de mapa grande
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#24221a",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa grande"
						src={mapUrl}
						width="100%"
						height="100%"
						sx={{
							border: 0,
							flex: 1,
						}}
						allowFullScreen
						loading="lazy"
					/>
				) : (
					<Box
						sx={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					</Box>
				)}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					zIndex: 2,
				}}
			>
				{headerText}
				{logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA - ancho completo */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					width: "100%",
					minWidth: 0,
					minHeight: 380,
					marginTop: "-70px",
					position: "relative",
					overflow: "hidden",
				}}
			>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #24221a",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{isOnline && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: "80px",
							right: "10px",
							backgroundColor: "#24221a",
							color: "#fff !important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa Kamakura"
						src={mapUrl}
						onLoad={() => setLoaded(true)}
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							minWidth: "100%",
							border: 0,
							display: "block",
						}}
					/>
				) : (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				)}

				{/* STICKER HASEDERA */}
				{hasederaSticker && !hasederaStickerError && (
					<img
						src={hasederaSticker}
						alt=""
						onError={() => setHasederaStickerError(true)}
						style={{
							width: 110,
							position: "absolute",
							bottom: -10,
							left: -18,
						}}
					/>
				)}

				{stickerHasederaText && (
					<Box
						sx={{
							position: "absolute",
							bottom: "50px",
							left: "80px",
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily} !important`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 200,
						}}
						component="div"
						dangerouslySetInnerHTML={{ __html: stickerHasederaText }}
					/>
				)}
			</Box>

			{/* EXPLICACIÓN ABAJO */}
			<Box
				data-block-id="text-1"
				sx={{
					padding: "12px 16px",
					fontFamily: `${fontFamily} !important`,
					fontSize: 12,
					backgroundColor: "#fff",
					color: "#333",
				}}
			>
				{explanationText}
			</Box>
		</Box>
	);
};

Template62.thumbnail = "/images/template-1.png";

export default Template62;
